/**
 * useOffline.ts — React hook for offline state and sync management
 * ─────────────────────────────────────────────────────────────────────────────
 * Usage in any component:
 *
 *   const { isOnline, pendingCount, isSyncing, syncNow } = useOffline();
 *
 *   isOnline     — true when the browser has network connectivity
 *   pendingCount — number of actions queued offline (badge on toolbar)
 *   isSyncing    — true while replayAllQueued() is running
 *   syncNow()    — manually trigger a sync replay (e.g. "Retry" button)
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { getPendingCount, replayAllQueued } from "../utils/syncQueue";

export interface OfflineState {
  isOnline:     boolean;
  pendingCount: number;
  isSyncing:    boolean;
  syncNow:      () => Promise<void>;
}

export function useOffline(): OfflineState {
  const [isOnline,     setIsOnline]     = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing,    setIsSyncing]    = useState(false);

  // Use a ref to avoid stale closure in the event listener
  const isSyncingRef = useRef(false);

  // ── Refresh badge count ─────────────────────────────────────────────────
  const refreshCount = useCallback(async () => {
    const count = await getPendingCount();
    setPendingCount(count);
  }, []);

  // ── Replay queue (called on 'online' event and manually) ─────────────────
  const syncNow = useCallback(async () => {
    if (isSyncingRef.current || !navigator.onLine) return;
    isSyncingRef.current = true;
    setIsSyncing(true);

    try {
      await replayAllQueued();
    } finally {
      isSyncingRef.current = false;
      setIsSyncing(false);
      refreshCount();
    }
  }, [refreshCount]);

  // ── Online / offline event listeners ─────────────────────────────────────
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Automatically replay queue when connectivity is restored.
      // This is the fallback for browsers that don't support Background Sync.
      syncNow();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online",  handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online",  handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [syncNow]);

  // ── Listen for SW messages (sync success notifications) ──────────────────
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "SYNC_SUCCESS") {
        refreshCount();
      }
    };
    navigator.serviceWorker.addEventListener("message", handleMessage);
    return () => navigator.serviceWorker.removeEventListener("message", handleMessage);
  }, [refreshCount]);

  // ── Initial count on mount ────────────────────────────────────────────────
  useEffect(() => {
    refreshCount();
    // Refresh count every 30s (in case SW replayed without messaging the client)
    const interval = setInterval(refreshCount, 30_000);
    return () => clearInterval(interval);
  }, [refreshCount]);

  return { isOnline, pendingCount, isSyncing, syncNow };
}
